import { useState, useRef, useEffect } from 'react';
import { Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn } from '@shared/utils/cn';

type Props = {
  suggestions: string[];
  onAddField: (fieldName: string) => void;
};

export function TargetFieldCombobox({ suggestions, onAddField }: Props) {
  const [value, setValue] = useState('');
  const [open, setOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const filteredSuggestions = value
    ? suggestions.filter((s) => s.toLowerCase().includes(value.toLowerCase()))
    : suggestions;

  const showDropdown = open && filteredSuggestions.length > 0;

  useEffect(() => {
    if (!showDropdown) {
      setSelectedIndex(0);
    }
  }, [showDropdown]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleAdd = (fieldName: string) => {
    onAddField(fieldName);
    setValue('');
    setOpen(false);
    inputRef.current?.blur();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!showDropdown) {
      if (e.key === 'Enter' && value.trim()) {
        handleAdd(value.trim());
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex((i) => (i + 1) % filteredSuggestions.length);
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex((i) => (i - 1 + filteredSuggestions.length) % filteredSuggestions.length);
        break;
      case 'Enter':
        e.preventDefault();
        handleAdd(filteredSuggestions[selectedIndex]);
        break;
      case 'Escape':
        e.preventDefault();
        setOpen(false);
        break;
    }
  };

  return (
    <div ref={containerRef} className="relative">
      <div className="flex items-center gap-2">
        <Input
          ref={inputRef}
          value={value}
          onChange={(e) => {
            setValue(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={handleKeyDown}
          placeholder="target.field"
          className="h-8 w-[260px]"
          autoComplete="off"
        />
        <Button
          size="sm"
          variant="outline"
          onClick={() => value.trim() && handleAdd(value.trim())}
          disabled={!value.trim()}
        >
          <Plus className="mr-1 h-3.5 w-3.5" /> Add
        </Button>
      </div>

      {showDropdown && (
        <div className="absolute top-full left-0 z-50 mt-1 w-[260px] rounded-md border bg-popover text-popover-foreground shadow-md animate-in fade-in-0 zoom-in-95">
          <div className="max-h-[300px] overflow-y-auto p-1">
            {filteredSuggestions.map((option, index) => (
              <button
                key={option}
                onClick={() => handleAdd(option)}
                onMouseEnter={() => setSelectedIndex(index)}
                className={cn(
                  'w-full text-left px-2 py-1.5 text-sm rounded-sm cursor-pointer outline-none',
                  'hover:bg-accent hover:text-accent-foreground',
                  selectedIndex === index && 'bg-accent text-accent-foreground'
                )}
              >
                {option}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
